import PublicLayout from "@/components/layout/PublicLayout";
import { ArrowRight, Compass, Factory, Sparkles, Users } from "lucide-react";
import { CONSULTATION_URL } from "@/data/constants";

const About = () => (
  <PublicLayout>
    {/* HERO */}
    <section className="ft-section-dark py-24">
      <div className="ft-container ft-fade-in max-w-4xl relative">
        <div className="text-[0.6875rem] uppercase tracking-[0.32em] text-[var(--ft-gold)] font-semibold mb-6">
          About FLOWTALIS™
        </div>
        <span className="ft-gold-rule-lg" />
        <h1 className="mt-6 text-white text-[clamp(2.25rem,4.5vw,3.75rem)] font-bold leading-[1.02] tracking-tight mb-8">
          A premium beverage manufacturer built with the discipline of an{" "}
          <span className="ft-serif text-[var(--ft-gold)]">enterprise operating company</span>.
        </h1>
        <p className="text-white/60 text-lg leading-relaxed max-w-2xl">
          FLOWTALIS was founded on a simple belief: beverage brands deserve manufacturing partners
          who operate with the same excellence they demand of their own product. From source, to
          formulation, to bottle, to shelf — every stage runs through one platform, one team, and
          one uncompromising standard.
        </p>
      </div>
    </section>

    {/* OUR STORY */}
    <section className="py-24 bg-white" data-testid="about-story">
      <div className="ft-container grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <div className="ft-eyebrow mb-4">Our Story</div>
          <span className="ft-gold-rule-lg" />
          <h2 className="mt-6 text-3xl md:text-4xl font-bold tracking-tight text-[var(--ft-black)]">
            Why we built <span className="ft-serif text-[var(--ft-gold)]">FLOWTALIS</span>.
          </h2>
        </div>
        <div className="lg:col-span-7 space-y-5 text-[var(--ft-charcoal)] leading-relaxed">
          <p>
            The private-label beverage industry has long felt fragmented — separate vendors for
            manufacturing, artwork, compliance, and logistics, each with their own timelines and
            standards. Founders trying to launch a brand were forced to become project managers
            across five suppliers before they ever poured their first bottle.
          </p>
          <p>
            FLOWTALIS was built to change that. We assembled a vetted network of FDA-compliant
            manufacturing partners, an in-house design and compliance team, and a client operating
            system — all under one roof, with one point of accountability. Whether you&apos;re
            launching your first pallet or scaling to national distribution, FLOWTALIS carries the
            operational weight so you can focus on brand and growth.
          </p>
        </div>
      </div>
    </section>

    {/* MISSION + VISION */}
    <section className="py-24 bg-[var(--ft-light-gray)]" data-testid="about-mission-vision">
      <div className="ft-container grid md:grid-cols-2 gap-6">
        <div className="ft-card">
          <div className="h-12 w-12 rounded-full flex items-center justify-center mb-5"
               style={{ background: "var(--ft-gold-soft)", color: "var(--ft-gold)" }}>
            <Compass size={22} strokeWidth={1.75} />
          </div>
          <div className="ft-eyebrow mb-2">Our Mission</div>
          <h3 className="text-2xl font-bold tracking-tight text-[var(--ft-black)] mb-3">
            From Source to Bottle™
          </h3>
          <p className="text-[var(--ft-muted)] leading-relaxed">
            Help entrepreneurs, retailers, gyms, hotels, restaurants, influencers, nonprofits, and
            corporations launch premium branded beverage products through a single, secure,
            end-to-end operating system.
          </p>
        </div>
        <div className="ft-card">
          <div className="h-12 w-12 rounded-full flex items-center justify-center mb-5"
               style={{ background: "var(--ft-gold-soft)", color: "var(--ft-gold)" }}>
            <Sparkles size={22} strokeWidth={1.75} />
          </div>
          <div className="ft-eyebrow mb-2">Our Vision</div>
          <h3 className="text-2xl font-bold tracking-tight text-[var(--ft-black)] mb-3">
            The trusted platform for every beverage founder.
          </h3>
          <p className="text-[var(--ft-muted)] leading-relaxed">
            To become the definitive private-label beverage platform in North America — the first
            call for anyone launching or scaling a branded beverage line, from concept through
            retail shelf.
          </p>
        </div>
      </div>
    </section>

    {/* MANUFACTURING PARTNER NETWORK */}
    <section className="py-24 bg-white" data-testid="about-partners">
      <div className="ft-container grid lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-5">
          <div className="h-12 w-12 rounded-full flex items-center justify-center mb-5"
               style={{ background: "var(--ft-gold-soft)", color: "var(--ft-gold)" }}>
            <Factory size={22} strokeWidth={1.75} />
          </div>
          <div className="ft-eyebrow mb-4">Manufacturing Partner Network</div>
          <span className="ft-gold-rule-lg" />
          <h2 className="mt-6 text-3xl md:text-4xl font-bold tracking-tight text-[var(--ft-black)]">
            A vetted network. <span className="ft-serif text-[var(--ft-gold)]">One point</span> of
            accountability.
          </h2>
        </div>
        <div className="lg:col-span-7 space-y-4 text-[var(--ft-charcoal)] leading-relaxed">
          <p>
            FLOWTALIS partners with a carefully selected roster of FDA-registered manufacturing
            facilities across the United States. Every facility is evaluated on performance,
            compliance, quality control, and capacity before it&apos;s cleared to produce for our
            clients.
          </p>
          <p>
            This partner-network model gives you the scale of a large manufacturer with the
            attention of a boutique brand studio. We match every project to the right facility for
            its category, volume, and packaging requirements — with FLOWTALIS serving as your
            single point of contact from artwork to reorder.
          </p>
        </div>
      </div>
    </section>

    {/* COMMITMENT */}
    <section className="py-24 bg-[var(--ft-light-gray)]" data-testid="about-commitment">
      <div className="ft-container grid lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-5">
          <div className="h-12 w-12 rounded-full flex items-center justify-center mb-5"
               style={{ background: "var(--ft-gold-soft)", color: "var(--ft-gold)" }}>
            <Users size={22} strokeWidth={1.75} />
          </div>
          <div className="ft-eyebrow mb-4">Our Commitment</div>
          <span className="ft-gold-rule-lg" />
          <h2 className="mt-6 text-3xl md:text-4xl font-bold tracking-tight text-[var(--ft-black)]">
            Built for the <span className="ft-serif text-[var(--ft-gold)]">founders</span> we serve.
          </h2>
        </div>
        <div className="lg:col-span-7 space-y-4 text-[var(--ft-charcoal)] leading-relaxed">
          <p>
            We believe launching a beverage brand should feel like a professional partnership — not
            a scramble. Every project runs through a documented pipeline stage. Every artwork
            revision has a version and an approver. Every case shipped has a compliance trail.
            Every client has a dedicated launch team.
          </p>
          <p>
            Whether you&apos;re a first-time founder taking a bold idea to market or an established
            operator scaling into new SKUs, FLOWTALIS is committed to matching the standards you
            hold for your own brand — with the operational rigor and executive polish of an
            enterprise partner.
          </p>
        </div>
      </div>
    </section>

    {/* CTA */}
    <section className="py-24 bg-white">
      <div className="ft-container text-center max-w-2xl">
        <h2 className="text-3xl font-bold tracking-tight mb-4">
          Launch your beverage brand with FLOWTALIS.
        </h2>
        <p className="text-[var(--ft-muted)] mb-8">
          Every consultation includes a category scoping, timeline estimate, and MOQ roadmap.
        </p>
        <a href={CONSULTATION_URL} target="_blank" rel="noopener noreferrer" className="ft-btn-primary" data-testid="about-cta">
          Book Consultation <ArrowRight size={16} />
        </a>
      </div>
    </section>
  </PublicLayout>
);

export default About;
